import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({ baseURL: BASE_URL, timeout: 30000 });

// Timeout long pour les appels IA (CV convert, score-cv) — jusqu'à 2 min
export const apiLong = axios.create({ baseURL: BASE_URL, timeout: 120000 });

const addAuth = (config) => {
  const token = localStorage.getItem('wib_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
};

const onError = (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('wib_token');
    localStorage.removeItem('wib_user');
    window.location.href = '/login';
  }
  return Promise.reject(err);
};

api.interceptors.request.use(addAuth);
api.interceptors.response.use((res) => res, onError);
apiLong.interceptors.request.use(addAuth);
apiLong.interceptors.response.use((res) => res, onError);

// ── Auth ─────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
};

// ── KPI Admin ─────────────────────────────────────────────────
export const kpiAPI = {
  dashboard: (params) => api.get('/admin/kpi/dashboard', { params }),
  activity:  ()       => api.get('/admin/kpi/activity'),
};

// ── Kanban ────────────────────────────────────────────────────
export const kanbanAPI = {
  getBoard: (params) => api.get('/ao-kanban/board', { params }),
  getFilters: () => api.get('/ao-kanban/filters'),
  getDetail: (id) => api.get(`/ao-kanban/${id}`),
  shortlist: (aoId, candidateIds) =>
    api.post('/ao-kanban/shortlist', { ao_id: aoId, candidate_ids: candidateIds }),
  unshortlist: (aoId, candidateIds) =>
    api.post('/ao-kanban/unshortlist', { ao_id: aoId, candidate_ids: candidateIds }),
  topProfil: (aoId, candidateIds) =>
    api.post('/ao-kanban/top-profils', { ao_id: aoId, candidate_ids: candidateIds }),
  untopProfil: (aoId, candidateIds) =>
    api.post('/ao-kanban/untop-profils', { ao_id: aoId, candidate_ids: candidateIds }),
  validateShortlist: (data) => api.post('/ao-kanban/validate-shortlist', data),
  validateTopProfil: (data) => api.post('/ao-kanban/validate-top-profil', data),
  reassign: (data) => api.post('/ao-kanban/reassign', data),
};

// ── AO Management ─────────────────────────────────────────────
export const aoAPI = {
  getNew: () => api.get('/ao-management/nouveaux'),
  getMine: () => api.get('/ao-management/mine'),

  create: (data) =>
    api.post('/ao-management/manual', {
      source_type: data.source_type || 'Manuel',
      job_title: data.job_title,
      client: data.client,
      location: data.location,
      contract_type: data.contract_type,
      work_mode: data.work_mode,
      experience_years: data.experience_years,
      education_level: data.education_level,
      tech_skills: data.tech_skills || [],
      soft_skills: data.soft_skills || [],
      detailed_description: data.detailed_description,
      raw_text: data.raw_text || data.detailed_description || '',
      commercial_contact: data.commercial_contact || '',
    }),

  takeCharge: (id) => api.post(`/ao-management/${id}/take`),
  takeSourcing: (id) => api.post(`/ao-management/${id}/take-sourcing`),
  sendToTA: (id, talentId) =>
    api.post(`/ao-management/${id}/assign-talent`, { talent_acquisition_id: talentId || null }),

  // Assigner un IA (Admin seulement)
  assignIA: (aoId, data) => api.post(`/ao-management/${aoId}/assign-ia`, data),

  // Envoyer au TA avec délai (IA / Admin)
  sendToTAWithDeadline: (aoId, data) => api.post(`/ao-management/${aoId}/send-to-ta-with-deadline`, data),

  // KPIs personnels
  reprocess:         (id)  => api.post(`/ao/worked/${id}/reprocess`),
  reprocessAllFailed: ()   => api.post('/ao/worked/reprocess-all-failed'),
  myKpis: () => api.get('/ao-management/my-kpis'),
  changeStatus: (id, status, candidateIds) =>
    api.post(`/ao-management/${id}/status`, {
      ao_avancement: status,
      ...(candidateIds?.length ? { candidate_ids: candidateIds } : {}),
    }),
  abandon: (id, comment) =>
    api.post(`/ao-management/${id}/abandon`, { comment }),
  myActivity: () => api.get('/ao-management/my-activity'),
  getStatusHistory: (id) => api.get(`/ao-management/${id}/status-history`),
  updateAO: (id, data) => api.put(`/ao-management/${id}`, data),

  // Supprimer un match candidat
  deleteMatch: (aoId, matchId) => api.delete(`/ao/${aoId}/matches/${matchId}`),

  // ── Recherche candidats par source ──────────────────────────
  // source = 'apec' | 'turnover' | 'linkedin' | 'all'
  searchCandidates: (aoId, source, numProfiles = 10) => {
    const sources = {
      use_apec:     source === 'apec'     || source === 'all',
      use_turnover: source === 'turnover' || source === 'all',
      use_linkedin: source === 'linkedin' || source === 'all',
    };
    return api.post(`/ao/${aoId}/candidates/search`, {
      sources,
      num_profiles_per_source: numProfiles,
    });
  },

  matchStats: (aoId) => api.get(`/ao/${aoId}/match-stats`),

  // Upload CV brut → scoring IA
  scoreCvManual: (aoId, file) => {
    const form = new FormData();
    form.append('file', file);
    return apiLong.post(`/ao/${aoId}/candidates/score-cv`, form);
  },
  scoreExisting: (aoId, candidateId) =>
    apiLong.post(`/ao/${aoId}/candidates/score-existing`, { candidate_id: candidateId }),
    getStatusHistoryBulk: (data) =>
    api.post('/ao-management/status-history/bulk', data),
};

// ── AO Matches (qualification / entretien) ────────────────────
export const matchAPI = {
  prequalifySoft: (matchId, report) =>
    api.post(`/ao-matches/${matchId}/prequalify-soft`, { report }),
  prequalifyHard: (matchId, report) =>
    api.post(`/ao-matches/${matchId}/prequalify-hard`, { report }),
  disqualify: (matchId, report) =>
    api.post(`/ao-matches/${matchId}/disqualify`, { report: report || null }),
  saveCallReport: (matchId, data) =>
    api.post(`/ao-matches/${matchId}/save-call-report`, data),
  sendEmail: (matchId, data) =>
    api.post(`/ao-matches/${matchId}/send-email`, data),
  callAI: (matchId, audioBlob) => {
    const form = new FormData();
    form.append('audio', audioBlob, 'recording.webm');
    return apiLong.post(`/ao-matches/${matchId}/call-ai`, form);
  },
  clearCallReport: (matchId) =>
    api.post(`/ao-matches/${matchId}/save-call-report`, { transcript: null, summary: null }),
  updateAIReport: (matchId, data) =>
    api.patch(`/ao-matches/${matchId}/update-ai-report`, data),
};

// ── Besoins ───────────────────────────────────────────────────
export const besoinsAPI = {
  list:      (params) => api.get('/besoins/', { params }),
  stats:     ()       => api.get('/besoins/stats'),
  get:       (id)     => api.get(`/besoins/${id}`),
  update:    (id, d)  => api.patch(`/besoins/${id}`, d),
  patch:     (id, d)  => api.patch(`/besoins/${id}`, d),
  importCsv: ()       => api.post('/besoins/import-csv'),
  importFile:(file)   => {
    const form = new FormData(); form.append('file', file);
    return api.post('/besoins/import-csv-upload', form);
  },
};

// ── Corbeille ─────────────────────────────────────────────────
export const corbeilleAPI = {
  mettreEnCorbeille: (aoId) => api.post(`/ao-corbeille/${aoId}/mettre-en-corbeille`),
  restaurer: (aoId) => api.post(`/ao-corbeille/${aoId}/restaurer`),
  supprimerDefinitivement: (aoId) => api.delete(`/ao-corbeille/${aoId}/supprimer-definitivement`),
};

// ── Candidates ────────────────────────────────────────────────
export const candidateAPI = {
  getOne: (id) => api.get(`/candidates/${id}`),
  getCvUrl: (id) => api.get(`/candidates/${id}/cv-url`),
  create: (data) => api.post('/candidates/', data),
  update: (id, data) => api.put(`/candidates/${id}`, data),
  patch: (id, data) => api.patch(`/candidates/${id}`, data),
  detail: (id) => api.get(`/candidates/${id}/detail`),
  extractFromCV: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiLong.post('/candidates/extract-from-cv', form);
  },
  uploadCv: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return apiLong.post(`/candidates/${id}/upload-cv`, form);
  },
  deleteCv: (id) => api.patch(`/candidates/${id}`, { cv_minio_path: null, cv_link: null }),
};

// ── Candidate Actions ─────────────────────────────────────────
export const candidateActionsAPI = {
  list: (candidateId) => api.get(`/candidate-actions/${candidateId}`),
  create: (data) => api.post('/candidate-actions/', data),
  update: (id, data) => api.put(`/candidate-actions/${id}`, data),
  delete: (id) => api.delete(`/candidate-actions/${id}`),
};

// ── Candidate History ─────────────────────────────────────────
export const candidateHistoryAPI = {
  list: (candidateId) => api.get(`/candidate-history/${candidateId}`),
};

// ── Candidate Positionings ────────────────────────────────────
export const candidatePositioningAPI = {
  list: (candidateId) => api.get(`/candidate-positionings/${candidateId}`),
  create: (data) => api.post('/candidate-positionings/', data),
  update: (id, data) => api.put(`/candidate-positionings/${id}`, data),
};

// ── CV ────────────────────────────────────────────────────────
export const cvAPI = {
  convert: (file, targetTitle) => {
    const form = new FormData();
    form.append('file', file);
    if (targetTitle) form.append('target_title', targetTitle);
    return apiLong.post('/cv/convert', form);
  },
  convertFromCandidate: (candidateId, targetTitle) =>
    apiLong.post(
      `/cv/convert-from-candidate/${candidateId}`,
      null,
      targetTitle ? { params: { target_title: targetTitle } } : {}
    ),
  save: (data) => api.post('/cv/save', data),
  update: (id, data) => api.put(`/cv/${id}`, data),
  list: (params) => api.get('/cv/', { params }),
  // Récupère les CV liés à un candidat (renvoie tableau vide si pas trouvé)
  listByCandidate: (candidateId) => api.get('/cv/', { params: { candidate_id: candidateId } }),
  getOne: (id) => api.get(`/cv/${id}`),
  regenerateDocx: (id) => api.post(`/cv/${id}/regenerate-docx`),
  sendToCandidate: (id, data) => api.post(`/cv/${id}/send-to-candidate`, data),
  translate: (id) => apiLong.post(`/cv/${id}/translate`),
  downloadByUrl: (url) => axios.get(url, { responseType: 'blob' }),
};

// ── Notifications ─────────────────────────────────────────────
export const notifAPI = {
  list: () => api.get('/notifications/'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  unreadCount: () => api.get('/notifications/unread-count'),
};

// ── Admin / Users ─────────────────────────────────────────────
export const adminAPI = {
  // Gestion utilisateurs
  listUsers:    ()           => api.get('/admin/users'),
  createUser:   (data)       => api.post('/admin/users', data),
  updateUser:   (id, data)   => api.put(`/admin/users/${id}`, data),
  deleteUser:   (id)         => api.delete(`/admin/users/${id}`),
  toggleActive: (id)         => api.post(`/admin/users/${id}/toggle-active`),
  resendInvite: (id)         => api.post(`/admin/users/${id}/resend-invite`),

  // Profil personnel
  getMe:           ()     => api.get('/admin/me'),
  updateMe:        (data) => api.put('/admin/me', data),
  changePassword:  (data) => api.post('/admin/me/change-password', data),

  // Set password via token email (public)
  setPasswordToken: (data) => api.post('/admin/set-password', data),
};

// ── Scraping AOs ──────────────────────────────────────────────
export const scrapingAPI = {
  status: () => api.get('/scraping/status'),
  run: (source) => api.post(`/scraping/run/${source}`),
};



export const resourcesAPI = {
  list:   (params) => api.get('/resources/', { params }),
  getOne: (id)     => api.get(`/resources/${id}`),
  create: (data)   => api.post('/resources/', data),
  update: (id, data) => api.put(`/resources/${id}`, data),
  delete: (id)     => api.delete(`/resources/${id}`),
  // Missions
  listMissions:   (resourceId)       => api.get(`/resources/${resourceId}/missions`),
  createMission:  (resourceId, data) => api.post(`/resources/${resourceId}/missions`, data),
  updateMission:  (missionId, data)  => api.put(`/resources/missions/${missionId}`, data),
  deleteMission:  (missionId)        => api.delete(`/resources/missions/${missionId}`),
  // Documents
  listDocuments:  (resourceId)       => api.get(`/resources/${resourceId}/documents`),
  createDocument: (resourceId, data) => api.post(`/resources/${resourceId}/documents`, data),
  uploadDocument: (resourceId, formData) => api.post(`/resources/${resourceId}/documents/upload`, formData, { headers:{ 'Content-Type':'multipart/form-data' } }),
  updateDocument: (documentId, data) => api.put(`/resources/documents/${documentId}`, data),
  deleteDocument: (documentId)       => api.delete(`/resources/documents/${documentId}`),
};
 
// ── CRM Companies ─────────────────────────────────────────────
export const companiesAPI = {
  list:   (params) => api.get('/companies/', { params: { limit: 1000, ...params } }),
  getOne: (id)     => api.get(`/companies/${id}`),
  create: (data)   => api.post('/companies/', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id)     => api.delete(`/companies/${id}`),
  // Contacts
  listContacts:   (companyId)       => api.get(`/companies/${companyId}/contacts`),
  createContact:  (companyId, data) => api.post(`/companies/${companyId}/contacts`, data),
  updateContact:  (contactId, data) => api.put(`/companies/contacts/${contactId}`, data),
  deleteContact:  (contactId)       => api.delete(`/companies/contacts/${contactId}`),
  // Besoins
  listNeeds:   (companyId)      => api.get(`/companies/${companyId}/needs`),
  createNeed:  (companyId, data)=> api.post(`/companies/${companyId}/needs`, data),
  updateNeed:  (needId, data)   => api.put(`/companies/needs/${needId}`, data),
  deleteNeed:  (needId)         => api.delete(`/companies/needs/${needId}`),
  // Actions CRM
  listActions:  (companyId)       => api.get(`/companies/${companyId}/actions`),
  createAction: (companyId, data) => api.post(`/companies/${companyId}/actions`, data),
  updateAction: (actionId, data)  => api.put(`/companies/actions/${actionId}`, data),
  deleteAction: (actionId)        => api.delete(`/companies/actions/${actionId}`),
};
 
// ── Pipeline candidats ────────────────────────────────────────
export const pipelineAPI = {
  list:             (params)         => api.get('/candidate-pipeline/', { params }),
  getOne:           (id)             => api.get(`/candidate-pipeline/${id}`),
  create:           (data)           => api.post('/candidate-pipeline/', data),
  addFromCandidate: (candidateId, params) => api.post(`/candidate-pipeline/from-candidate/${candidateId}`, null, { params }),
  scheduleInterview: (candidateId, data)  => api.post(`/candidate-pipeline/from-candidate/${candidateId}/entretien`, data),
  update:           (id, data)       => api.put(`/candidate-pipeline/${id}`, data),
  delete:           (id)             => api.delete(`/candidate-pipeline/${id}`),
  checkCandidates:  (ids)            => api.get('/candidate-pipeline/check', { params: { ids: ids.join(',') } }),
  listDocuments:    (entryId)        => api.get(`/candidate-pipeline/${entryId}/documents`),
  uploadDocument:   (entryId, form)  => api.post(`/candidate-pipeline/${entryId}/documents/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument:   (docId)          => api.delete(`/candidate-pipeline/documents/${docId}`),
  // Actions
  listActions:  (entryId)       => api.get(`/candidate-pipeline/${entryId}/actions`),
  createAction: (entryId, data) => api.post(`/candidate-pipeline/${entryId}/actions`, data),
  updateAction: (actionId, data)=> api.put(`/candidate-pipeline/actions/${actionId}`, data),
  deleteAction: (actionId)      => api.delete(`/candidate-pipeline/actions/${actionId}`),
};
 


// ── Calendar ──────────────────────────────────────────────────
export const calendarAPI = {
  getEvents: () => api.get('/calendar/events'),
};

// ── Référent Technique ────────────────────────────────────────
export const rtAPI = {
  // Rapports
  createReport:   (data)                      => api.post('/rt-reports/', data),
  updateReport:   (id, data)                  => api.put(`/rt-reports/${id}`, data),
  submitReport:   (id)                        => api.post(`/rt-reports/${id}/submit`),
  disqualifyReport: (id)                      => api.post(`/rt-reports/${id}/disqualify`),
  getReport:      (id)                        => api.get(`/rt-reports/${id}`),
  getMyReports:   ()                          => api.get('/rt-reports/my'),
  getReportsForCandidateOnAO: (aoId, candId) => api.get(`/rt-reports/ao/${aoId}/candidate/${candId}`),
  getReportsForCandidate: (candId)            => api.get(`/rt-reports/candidate/${candId}`),
  getAOReports:   (aoId)                      => api.get(`/rt-reports/ao/${aoId}`),
  // Questions IA
  generateQuestions:  (aoId, candId, nb = 10) => api.post('/rt-reports/ai/questions', { ao_id: aoId, candidate_id: candId }, { params: { nb_questions: nb }, timeout: 90000 }),
  evaluateAnswers:    (aoId, candId, questions) => apiLong.post('/rt-reports/ta/evaluate-answers', { ao_id: aoId, candidate_id: candId, questions }, { timeout: 120000 }),
  getTAQuestions:     (aoId, candId) => api.get('/rt-reports/ta/questions', { params: { ao_id: aoId, candidate_id: candId } }),
  saveTAQuestions:    (aoId, candId, questions) => api.put('/rt-reports/ta/questions', { ao_id: aoId, candidate_id: candId, questions }),
  // Score
  changeScore:    (data)        => api.post('/rt-score-changes/', data),
  getScoreHistory:(candId)      => api.get(`/rt-score-changes/candidate/${candId}`),
  // AO — assignation RT
  assignReferent: (aoId, rtId)  => api.post(`/ao-management/${aoId}/assign-referent`, { referent_technique_id: rtId }),
  getAOReferent:  (aoId)        => api.get(`/ao-management/${aoId}/referent`),
  // RT — mes AOs
  getMyAOs:       ()            => api.get('/ao-management/rt/my-aos'),
  // Liste des RTs disponibles
  listRTs:        ()            => api.get('/users/technical-referents'),
};

// ── Business Units ────────────────────────────────────────────
export const buAPI = {
  list:         ()              => api.get('/business-units/'),
  getMyBUs:     ()              => api.get('/business-units/my'),
  get:          (id)            => api.get(`/business-units/${id}`),
  create:       (data)          => api.post('/business-units/', data),
  update:       (id, data)      => api.put(`/business-units/${id}`, data),
  remove:       (id)            => api.delete(`/business-units/${id}`),
  addMember:       (id, data)   => api.post(`/business-units/${id}/members`, data),
  removeMember:    (id, userId) => api.delete(`/business-units/${id}/members/${userId}`),
  getMyBUTAUsers:  ()           => api.get('/business-units/my-bu-ta-users'),
  getMyBUTeam:     ()           => api.get('/business-units/my-bu-team'),
};

export const positionnementAPI = {
  list:   (params) => api.get('/positionnements/', { params }),
  get:    (id)     => api.get(`/positionnements/${id}`),
  stats:  ()       => api.get('/positionnements/stats'),
  update: (id, d)  => api.patch(`/positionnements/${id}`, d),
};

export const actionsAPI = {
  list:   (params)    => api.get('/actions/', { params }),
  get:    (id)        => api.get(`/actions/${id}`),
  create: (data)      => api.post('/actions/', data),
  patch:  (id, data)  => api.patch(`/actions/${id}`, data),
};

export const contactsAPI = {
  list:   (params)     => api.get('/contacts/', { params }),
  get:    (id)         => api.get(`/contacts/${id}`),
  update: (id, data)   => api.patch(`/contacts/${id}`, data),
};

export const projectsAPI = {
  list:   (params) => api.get('/projects/', { params }),
  get:    (id)     => api.get(`/projects/${id}`),
  create: (data)   => api.post('/projects/', data),
  update: (id, d)  => api.put(`/projects/${id}`, d),
  remove: (id)     => api.delete(`/projects/${id}`),
  // Prestations
  createPrestation: (id, data)      => api.post(`/projects/${id}/prestations`, data),
  updatePrestation: (id, pid, data) => api.put(`/projects/${id}/prestations/${pid}`, data),
  deletePrestation: (id, pid)       => api.delete(`/projects/${id}/prestations/${pid}`),
  // Actions
  listActions:   (id)         => api.get(`/projects/${id}/actions`),
  createAction:  (id, data)   => api.post(`/projects/${id}/actions`, data),
  updateAction:  (id, aid, d) => api.put(`/projects/${id}/actions/${aid}`, d),
  deleteAction:  (id, aid)    => api.delete(`/projects/${id}/actions/${aid}`),
  // Documents
  listDocs:      (id)         => api.get(`/projects/${id}/documents`),
  uploadDoc:     (id, form)   => api.post(`/projects/${id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDoc:     (id, did)    => api.delete(`/projects/${id}/documents/${did}`),
};

export const notesFraisAPI = {
  list:     (params) => api.get('/notes-frais/', { params }),
  stats:    ()       => api.get('/notes-frais/me/stats'),
  get:      (id)     => api.get(`/notes-frais/${id}`),
  create:   (fd)     => api.post('/notes-frais/', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:   (id, fd) => api.put(`/notes-frais/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove:   (id)     => api.delete(`/notes-frais/${id}`),
  submit:   (id)     => api.post(`/notes-frais/${id}/submit`),
  validate: (id, commentaire) => api.post(`/notes-frais/${id}/validate`, commentaire ? { commentaire_valideur: commentaire } : {}),
  reject:   (id, commentaire) => api.post(`/notes-frais/${id}/reject`,   commentaire ? { commentaire_valideur: commentaire } : {}),
  justificatifUrl:  (id) => `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/notes-frais/${id}/justificatif`,
  getJustificatif: (id) => api.get(`/notes-frais/${id}/justificatif`),
  cancel:   (id)     => api.post(`/notes-frais/${id}/cancel`),
};

export const imposedLeavesAPI = {
  list:         ()         => api.get('/absences-imposees/'),
  get:          (id)       => api.get(`/absences-imposees/${id}`),
  create:       (data)     => api.post('/absences-imposees/', data),
  update:       (id, data) => api.put(`/absences-imposees/${id}`, data),
  remove:       (id)       => api.delete(`/absences-imposees/${id}`),
  listSalaries: ()         => api.get('/absences-imposees/salaries'),
};

export const absencesAPI = {
  balance:  (annee)      => api.get('/absences/balance', { params: annee ? { annee } : {} }),
  list:     (params)     => api.get('/absences/', { params }),
  simulate: (data)       => api.post('/absences/simulate', data),
  create:   (data)       => api.post('/absences/', data),
  submit:   (id)         => api.post(`/absences/${id}/submit`),
  cancel:   (id)         => api.post(`/absences/${id}/cancel`),
  validate: (id, comment) => api.post(`/absences/${id}/validate`, { commentaire_rh: comment || null }),
  refuse:   (id, comment) => api.post(`/absences/${id}/refuse`,   { commentaire_rh: comment || null }),
};

export const timesheetAPI = {
  list:          (params)       => api.get('/timesheets/', { params }),
  getMine:       (annee, mois)  => api.get(`/timesheets/me/${annee}/${mois}`),
  reset:         (annee, mois)  => api.delete(`/timesheets/me/${annee}/${mois}`),
  get:           (id)           => api.get(`/timesheets/${id}`),
  save:          (id, data)     => api.put(`/timesheets/${id}/save`, data),
  submit:        (id)           => api.post(`/timesheets/${id}/submit`),
  validate:      (id, data)     => api.post(`/timesheets/${id}/validate`, data),
  reject:        (id, data)     => api.post(`/timesheets/${id}/reject`, data),
  getHolidays:   (pays, annee, mois) => api.get('/timesheets/holidays', { params: { pays, annee, mois } }),
};

export const emailTemplatesAPI = {
  list:   ()           => api.get('/email-templates/'),
  create: (data)       => api.post('/email-templates/', data),
  update: (id, data)   => api.put(`/email-templates/${id}`, data),
  remove: (id)         => api.delete(`/email-templates/${id}`),
};

export const importAPI = {
  upload: (type, formData) => api.post(
    `/admin/import/${type}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }
  ),
};

export default api;